import { clientConfigObj, clientConfigPath, defaultClientConfig, type ClientBranding, type ClientConfig, type ClientInvoice } from './clientConfigObj';

export { defaultClientConfig } from './clientConfigObj';

function resolveConfigPath(configPathOrKey?: string): string | undefined {
  if (!configPathOrKey?.trim()) {
    return undefined;
  }

  const normalized = configPathOrKey.trim();
  return clientConfigPath[normalized as keyof typeof clientConfigPath] ?? normalized;
}

function mergeConfig(partial: Partial<ClientConfig>): ClientConfig {
  const base = defaultClientConfig;
  const branding: Partial<ClientBranding> = partial.branding ?? {};
  const invoice: Partial<ClientInvoice> = partial.invoice ?? {};

  return {
    clientKey: partial.clientKey || base.clientKey,
    branding: {
      appName: branding.appName || base.branding.appName,
      companyName: branding.companyName || base.branding.companyName,
      logo: branding.logo || base.branding.logo,
      footerLogo: branding.footerLogo || base.branding.footerLogo,
      lableBottomLogo: typeof branding.lableBottomLogo === 'boolean' ? branding.lableBottomLogo : base.branding.lableBottomLogo,
      sidebarLogoExpanded: branding.sidebarLogoExpanded || base.branding.sidebarLogoExpanded,
      sidebarLogoCollapsed: branding.sidebarLogoCollapsed || base.branding.sidebarLogoCollapsed,
      logoDark: branding.logoDark || base.branding.logoDark,
      sidebarLogoExpandedDark: branding.sidebarLogoExpandedDark || base.branding.sidebarLogoExpandedDark,
      favicon: branding.favicon || base.branding.favicon
    },
    invoice: {
      legalCompanyName: invoice.legalCompanyName || base.invoice.legalCompanyName,
      gstNumber: invoice.gstNumber || base.invoice.gstNumber,
      registeredOffice: invoice.registeredOffice || base.invoice.registeredOffice,
      supportContact: invoice.supportContact || base.invoice.supportContact
    },
    paymentGateway: partial.paymentGateway ?? base.paymentGateway,
    wocomAppName: partial.wocomAppName ?? base.wocomAppName,
    shopifyURL: partial.shopifyURL ?? base.shopifyURL
  };
}

export function getClientConfigByKey(clientKey?: string): ClientConfig {
  if (!clientKey) {
    return defaultClientConfig;
  }

  return clientConfigObj[clientKey] ?? defaultClientConfig;
}

export async function loadClientConfig(): Promise<ClientConfig> {
  const resolvedPath = resolveConfigPath(import.meta.env.VITE_CLIENT_CONFIG_PATH as string | undefined);

  if (!resolvedPath) {
    return defaultClientConfig;
  }

  try {
    const response = await fetch(resolvedPath, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(String(response.status));
    }

    const json = (await response.json()) as Partial<ClientConfig>;
    return mergeConfig(json);
  } catch {
    return defaultClientConfig;
  }
}

export function applyClientConfigToDocument(clientConfig: ClientConfig) {
  if (typeof document === 'undefined') {
    return;
  }

  document.title = clientConfig.branding.appName;

  const faviconHref = clientConfig.branding.favicon || defaultClientConfig.branding.favicon;
  let faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

  if (!faviconLink) {
    faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    document.head.appendChild(faviconLink);
  }

  faviconLink.href = faviconHref;
}
